import {Component, OnDestroy, OnInit} from '@angular/core';
import {User} from '../../user/user';
import {AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {DbServerErrors} from "../../shared/interceptors/db-server-errors";
import {DbResponse} from "../../shared/interceptors/db-response";
import {NotifierComponent} from "../../shared/components/notifier/notifier.component";
import {Notification} from "../../shared/components/notifier/notification";
import {NotificationType} from "../../shared/components/notifier/notification-type";
import {ActivatedRoute} from '@angular/router';
import * as fromStore from "../../store";
import {Subscription} from 'rxjs';
import {Store} from '@ngrx/store';
import {filter, first, switchMap} from 'rxjs';
import {WorkspaceService} from "../workspace.service";
import {Utils} from "../../shared/utils/utils";
import {DbValidators} from "../../shared/validators/DbValidators";
import Locations from "../../../assets/json/locations_new.json";
import {
  Countries
} from "./countries";
import {IDsDropdownOption} from "../../shared/components/ds-dropdown/ds-dropdown-option";



@Component({
    selector: 'db-billing-details',
    templateUrl: './billing-details.component.html',
    styleUrls: ['./billing-details.component.scss'],
    standalone: false
})
export class BillingDetailsComponent implements OnInit, OnDestroy {

  private _subscriptions: Subscription[];
  public loading: boolean;
  private _user: User;
  public billingInfoForm: FormGroup;
  public serverErrors: DbServerErrors;
  public isMobile: boolean;
  public hasServerFieldErrors: boolean;
  public validPhoneNumber: boolean;
  public billingData: any;
  public statesOptions = [];
  public countryOptions = [];

  constructor(
    private _route: ActivatedRoute,
    private _store: Store<fromStore.DbStoreState>,
    private _workspaceService: WorkspaceService
  ) {}

  ngOnInit() {
    this._user = this._route.snapshot.data['user'];
    this.billingData = this._route.snapshot.data['billingInfo'];
    this.isMobile = window.innerWidth < 991;
    this.countryOptions = this._mapDropdown(Countries, false);
    this._setFormOptions();
    this._addEventHandlers();
  }

  private _setFormOptions(): void {
    this.billingInfoForm = new FormGroup({
      'firstName': new FormControl(this.billingData && this.billingData.firstName || this._user.firstName || '', [
        Utils.patternValidator(/[-a-zA-Z0-9@:%_+.~#?&\/=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_+.~#?&\/=]*)?/, { link: true }, true)
      ]),
      'lastName': new FormControl(this.billingData && this.billingData.lastName || this._user.lastName || '', [
        Utils.patternValidator(/[-a-zA-Z0-9@:%_+.~#?&\/=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_+.~#?&\/=]*)?/, { link: true }, true)
      ]),
      'email': new FormControl(this.billingData && this.billingData.email || this._user.email || '', [DbValidators.trim, Validators.email, Validators.pattern(/^[+_\-a-zA-Z0-9]+(\.[+_\-a-zA-Z0-9]+)*@[+_\-a-zA-Z0-9]+(\.[+_\-A-Za-z0-9]+)*(\.[+_\-A-Za-z]{2,24})$/)]),
      'phoneNumber': new FormControl(this.billingData && this.billingData.phoneNumber || '', [Validators.pattern(/^\+([0-9]){1,3}([0-9] ?){5,14}$/)]),
      'companyName': new FormControl(this.billingData && this.billingData.companyName || '', [Utils.patternValidator(/^[a-zA-Z0-9 .\-]*$/)]),
      'companyCountry': new FormControl(this.billingData && this.billingData.companyCountry || ''),
      'state': new FormControl(this.billingData && this.billingData.state || '', [Validators.pattern(/^[a-zA-Z -]*$/)]),
      'companyCity': new FormControl(this.billingData && this.billingData.companyCity || '', [Validators.pattern(/^[a-zA-Z -.]*$/)]),
      'companyAddressLine1': new FormControl(this.billingData && this.billingData.companyAddressLine1 || '', [Validators.pattern(/^[a-zA-Z0-9 -./]*$/)]),
      'companyAddressLine2': new FormControl(this.billingData && this.billingData.companyAddressLine2 || '', [Validators.pattern(/^[a-zA-Z0-9 -./]*$/)]),
      'companyZipCode': new FormControl(this.billingData && this.billingData.companyZipCode || '', [DbValidators.trim, Validators.pattern(/^[a-zA-Z0-9 -]*$/)]),
      'companyVat': new FormControl(this.billingData && this.billingData.companyVat || '', [Validators.pattern(
        '^((AT)?U[0-9]{8}|(BE)?0[0-9]{9}|(BG)?[0-9]{9,10}|(CY)?[0-9]{8}L|(CZ)?[0-9]{8,10}|(DE)?[0-9]{9}|(DK)?[0-9]{8}|(EE)?[0-9]{9}|(EL|GR)?[0-9]{9}|(ES)?[0-9A-Z][0-9]{7}[0-9A-Z]|(FI)?[0-9]{8}|(FR)?[0-9A-Z]{2}[0-9]{9}|(GB)?([0-9]{9}([0-9]{3})?|[A-Z]{2}[0-9]{3})|(HU)?[0-9]{8}|(IE)?[0-9]S[0-9]{5}L|(IT)?[0-9]{11}|(LT)?([0-9]{9}|[0-9]{12})|(LU)?[0-9]{8}|(LV)?[0-9]{11}|(MT)?[0-9]{8}|(NL)?[0-9]{9}B[0-9]{2}|(PL)?[0-9]{10}|(PT)?[0-9]{9}|(RO)?[0-9]{2,10}|(SE)?[0-9]{12}|(SI)?[0-9]{8}|(SK)?[0-9]{10})$')]),
    });
  }


  private _setLocationDropdowns(countryCode) {
    const country = Locations.find(item => item.iso2 === countryCode);
    this.statesOptions = country && this._mapDropdown(country.states) || [];
  }

  private _mapDropdown(data, useName = true): IDsDropdownOption[] {
    return data.map(({name, iso2}) => ({label: name, value: useName ? name : iso2}));
  }

  private _getStatesByCountryCode(countryValue): void {
    this._setLocationDropdowns(countryValue);
  }

  private _addEventHandlers(): void {
    this._subscriptions = [];

    this._subscriptions.push(this.billingInfoForm.controls['companyCountry'].valueChanges.subscribe((item): void => {
      this._getStatesByCountryCode(item);
    }));

    this._subscriptions.push(
      this._store.select(fromStore.getBillingInfoLoaded)
        .pipe(
          filter((loaded: boolean) => loaded),
          switchMap(() => this._store.select(fromStore.getBillingInfo)),
          first()
        )
        .subscribe((billingInfo: any) => {
          this.billingData = billingInfo;
          if (billingInfo.companyCountry) {
            this._getStatesByCountryCode(billingInfo?.companyCountry);
          }
        })
    );
  }

  public searchOptions(options, value): void {
    return options.filter(country => country.title.includes(value));
  }

  ngOnDestroy(): void {
    this.removeEventHandlers();
  }

  private removeEventHandlers(): void {
    this._subscriptions && this._subscriptions.length && this._subscriptions.forEach((subscription: Subscription): void => {
      subscription.unsubscribe();
    });

    this._subscriptions = null;
  }

  public onWorkspaceUpdate(): void {
    this.serverErrors = null;
    if (this.billingInfoForm.valid) {
      this.loading = true;
      this._workspaceService.updateBillingInfo(this.billingInfoForm.value)
      .pipe(filter((response: DbResponse): boolean => !!response),
        first())
        .subscribe((response: DbResponse): void => {
          this.loading = false;

          if (response.success) {
            NotifierComponent.notify(new Notification(response.message, NotificationType.SUCCESS));
            this._store.dispatch(new fromStore.LoadBillingInfoSuccess(this.billingInfoForm.value));
          }
          else {
            this.serverErrors = response.serverErrors;
            /*this.hasServerFieldErrors = !!Utils.intersect(Object.keys(this.serverErrors.fields), ['name', 'first_name', 'last_name', 'email', 'password', 'stripe_token']).length;*/
            if (!this.hasServerFieldErrors) {
              NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
            }
          }
        });
    }
  }

  public patternValidator(regex: RegExp, error: ValidationErrors, reverse = false): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      const valid = reverse ? !regex.test(control.value) : regex.test(control.value);
      return valid ? null : error;
    };
  }

}
